/*
  GD Blueprint Divider — shared engine for the brand pixel dividers.

  Any element with [data-gd-dither] is a divider strip. Only the pixel shape is
  ever painted: everything around it stays transparent. The shape is drawn the
  way a designer would build it: dotted construction guides slide out, pens trace
  the outline with square anchor handles and measurement redlines, then the shape
  and its edge fill in. The blueprint ink has its own colour, so it never takes on
  the colour or image of the section.

  Read from the strip (CSS custom properties, usually set on a wrapper):
    --sd-d-cell     pixel block size in px          --sd-d-radius  corner radius %
    --sd-d-fill     the divider colour              --sd-d-anim    0 = no animation
    --sd-d-style    fill style (see FILLS)          --sd-d-measure 1 = measurement redlines
    --sd-d-ink      blueprint ink ('auto' = brand blue on light pages, white on dark)
    --sd-d-grid     1 = graph-paper grid while drawing
  Attributes: data-rows; data-dir: 'down' = a start divider (the shape rises out of
  the part below it), 'up' = an end divider (the shape hangs from the part above);
  data-fill="zone" = paint the image of the enclosing [data-gd-zone] instead of a
  colour; data-seed / data-seed-key = pattern variation.

  A wrapper with [data-gd-auto-fill] takes its colour from the neighbouring section
  (the one below for a start divider, the one above for an end divider).

  Each strip plays once per page load, as it is scrolled down into view. Reached
  by scrolling up, it is simply already there.
*/
(function () {
  if (window.GDBP) { window.GDBP.mountAll(document); return; }

  /* ---------------- small helpers ---------------- */
  function hash(x, y, s) {
    var h = (x * 374761393 + y * 668265263 + s * 2246822519) | 0;
    h = (h ^ (h >>> 13)) >>> 0;
    h = (Math.imul(h, 1274126177)) >>> 0;
    return (h >>> 8) / 16777216;
  }
  function strHash(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
    return (h >>> 0) % 100000 + 1;
  }
  function clamp01(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function easeInOutSine(t) { return -(Math.cos(Math.PI * t) - 1) / 2; }
  function easeInOutCubic(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  /* Soft spring settle: 0 -> 1 with one gentle overshoot */
  function spring(t) { return t >= 1 ? 1 : 1 - Math.exp(-6 * t) * Math.cos(9 * t); }

  function parseColor(c) {
    var m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s\/]+([\d.]+))?/.exec(c || '');
    if (m) return { r: +m[1], g: +m[2], b: +m[3], a: m[4] === undefined ? 1 : +m[4] };
    var hx = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((c || '').trim());
    if (hx) {
      var v = hx[1];
      if (v.length === 3) v = v[0] + v[0] + v[1] + v[1] + v[2] + v[2];
      return { r: parseInt(v.slice(0, 2), 16), g: parseInt(v.slice(2, 4), 16), b: parseInt(v.slice(4, 6), 16), a: 1 };
    }
    return null;
  }
  function luminance(c) {
    var p = parseColor(c);
    if (!p) return 1;
    function ch(v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }
    return 0.2126 * ch(p.r) + 0.7152 * ch(p.g) + 0.0722 * ch(p.b);
  }

  function roundedCellR(path, x, y, w, h, a, b, c, d) {
    path.moveTo(x + a, y);
    path.lineTo(x + w - b, y);
    if (b) path.arcTo(x + w, y, x + w, y + b, b); else path.lineTo(x + w, y);
    path.lineTo(x + w, y + h - c);
    if (c) path.arcTo(x + w, y + h, x + w - c, y + h, c); else path.lineTo(x + w, y + h);
    path.lineTo(x + d, y + h);
    if (d) path.arcTo(x, y + h, x, y + h - d, d); else path.lineTo(x, y + h);
    path.lineTo(x, y + a);
    if (a) path.arcTo(x, y, x + a, y, a); else path.lineTo(x, y);
    path.closePath();
  }

  function filletPath(path, x, y, ex, ey, rr) {
    /* Quarter square at the vertex minus a quarter circle: a smooth concave join */
    var cx = x + rr * ex, cy = y + rr * ey;
    var a1 = Math.atan2(-ey, 0);
    var a2 = Math.atan2(0, -ex);
    var ccw = ((a2 - a1 + Math.PI * 2) % (Math.PI * 2)) > Math.PI;
    path.moveTo(x, y);
    path.lineTo(cx, y);
    path.arc(cx, cy, rr, a1, a2, ccw);
    path.closePath();
  }

  /* ---------------- automatic colours from the neighbouring sections ---------------- */
  function visibleBg(node, fromEnd) {
    var n = node, depth = 0;
    while (n && depth < 8) {
      var cs = getComputedStyle(n);
      var p = parseColor(cs.backgroundColor);
      if (p && p.a > 0.5) return cs.backgroundColor;
      var kids = Array.prototype.filter.call(n.children, function (k) {
        return !/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(k.tagName) && k.getBoundingClientRect().height > 0;
      });
      n = fromEnd ? kids[kids.length - 1] : kids[0];
      depth++;
    }
    return null;
  }
  function pageBg() {
    var b = getComputedStyle(document.body).backgroundColor, p = parseColor(b);
    if (p && p.a > 0.5) return b;
    b = getComputedStyle(document.documentElement).backgroundColor; p = parseColor(b);
    return p && p.a > 0.5 ? b : '#ffffff';
  }
  function neighbour(section, dir) {
    var n = section;
    do { n = dir < 0 ? n.previousElementSibling : n.nextElementSibling; }
    while (n && (n.getBoundingClientRect().height === 0 || /^(SCRIPT|STYLE|TEMPLATE)$/.test(n.tagName)));
    return n;
  }
  function applyAutoFill(wrap, el) {
    var section = wrap.closest('.shopify-section') || wrap;
    var up = el.getAttribute('data-dir') === 'up';
    var n = neighbour(section, up ? -1 : 1);
    wrap.style.setProperty('--sd-d-fill', (n && visibleBg(n, up)) || '#0048FF');
  }
  /* what shows through around the shape: the first solid background behind the strip */
  function backdropOf(el) {
    var n = el.parentElement;
    while (n && n !== document.documentElement) {
      if (!n.hasAttribute('data-gd-zone')) {
        var p = parseColor(getComputedStyle(n).backgroundColor);
        if (p && p.a > 0.5) return getComputedStyle(n).backgroundColor;
      }
      n = n.parentElement;
    }
    return pageBg();
  }

  /* images for strips that continue a section's background picture */
  var imgCache = {};
  function zoneSource(el) {
    var zone = el.closest('[data-gd-zone]');
    var bg = zone && zone.querySelector('[data-gd-zone-bg]');
    if (!bg) return null;
    var m = /url\(["']?([^"')]+)["']?\)/.exec(getComputedStyle(bg).backgroundImage || '');
    var src = { bg: bg, colour: getComputedStyle(bg).backgroundColor, img: null, overlay: null };
    if (m) {
      var url = m[1];
      if (!imgCache[url]) {
        var im = new Image();
        im.decoding = 'async';
        im.onload = function () { mountAll(document); };
        im.src = url;
        imgCache[url] = im;
      }
      src.img = imgCache[url];
    }
    var ov = bg.querySelector('[data-gd-zone-overlay]');
    if (ov) {
      var oc = getComputedStyle(ov);
      src.overlay = { colour: oc.backgroundColor, alpha: parseFloat(oc.opacity) || 0 };
    }
    return src;
  }

  /* ---------------- layout: the finished pixel shape (pure geometry) ---------------- */
  function cssVar(cs, name, fallback) {
    var v = (cs.getPropertyValue(name) || '').trim();
    return v === '' ? fallback : v;
  }

  function layoutDither(el) {
    var cs = getComputedStyle(el);
    var cell = Math.round(parseFloat(cssVar(cs, '--sd-d-cell', '32')) || 32);
    var radiusPct = parseFloat(cssVar(cs, '--sd-d-radius', '22'));
    if (isNaN(radiusPct)) radiusPct = 22;
    var rows = parseInt(el.getAttribute('data-rows'), 10) || 5;
    var dir = el.getAttribute('data-dir') === 'up' ? 'up' : 'down';
    var color = cssVar(cs, '--sd-d-fill', '#0048FF');
    var zone = el.getAttribute('data-fill') === 'zone' ? zoneSource(el) : null;
    var animate = cssVar(cs, '--sd-d-anim', '1') !== '0';
    var style = cssVar(cs, '--sd-d-style', 'sealed');
    var backdrop = backdropOf(el);
    var ink = cssVar(cs, '--sd-d-ink', 'auto');
    if (ink === 'auto' || !parseColor(ink)) ink = luminance(backdrop) > 0.35 ? '#0048FF' : '#FFFFFF';
    var seed = parseInt(el.getAttribute('data-seed'), 10) || strHash(el.getAttribute('data-seed-key') || 'gd');
    if (!el.clientWidth || !el.clientHeight) return null;
    return layoutFrom({
      el: el, w: el.clientWidth, h: el.clientHeight, cell: cell, radius: radiusPct, rows: rows, dir: dir,
      color: color, zone: zone, animate: animate, style: style, backdrop: backdrop, ink: ink, seed: seed,
      grid: cssVar(cs, '--sd-d-grid', '0') === '1', measure: cssVar(cs, '--sd-d-measure', '1') === '1'
    });
  }

  /*
    The shape itself, from plain options. mass: 'full' makes the colour cover nearly
    the whole strip with only the bottom few rows ragged (used for panels such as the
    mobile menu); otherwise the stepped edge takes up most of the strip.
  */
  function layoutFrom(o) {
    var el = o.el || null, w = o.w, h = o.h, cell = Math.round(o.cell), radiusPct = o.radius;
    var rows = o.rows, dir = o.dir, color = o.color, zone = o.zone || null, animate = o.animate !== false;
    var style = o.style || 'sealed', backdrop = o.backdrop || pageBg(), ink = o.ink, seed = o.seed || 1;
    if (!ink || ink === 'auto' || !parseColor(ink)) ink = luminance(backdrop) > 0.35 ? '#0048FF' : '#FFFFFF';

    var cols = Math.ceil(w / cell);
    var rowH = h / rows;
    var r = Math.min(cell, rowH) * Math.min(radiusPct, 50) / 100;
    var rf = Math.min(r * 0.9, Math.min(cell, rowH) / 2);
    var n = 0;
    function rnd() { n++; return hash(n * 7 + 3, n * 13 + 5, seed); }

    /* Grid in "rising" coordinates: row 0 is the edge touching the next band. */
    var grid = [], holes = [];
    for (var gy = 0; gy < rows; gy++) { grid.push(new Array(cols).fill(false)); holes.push(new Array(cols).fill(false)); }
    function set(x, y) { if (x >= 0 && x < cols && y >= 0 && y < rows) grid[y][x] = true; }

    /* 1. Stepped skyline, moving in runs so it reads as chunky steps, not noise */
    var minH = 1, maxH = rows <= 4 ? Math.max(1, rows - 1) : rows - 2;   /* short strips still get steps */
    if (o.mass === 'full') { minH = Math.max(1, rows - 4); maxH = Math.max(minH, rows - 2); }
    var heights = [], cur = Math.round((minH + maxH) / 2), c = 0;
    while (c < cols) {
      var run = 1 + Math.floor(rnd() * 4);
      cur = Math.max(minH, Math.min(maxH, cur + Math.floor(rnd() * 5) - 2));
      for (var i = 0; i < run && c < cols; i++, c++) heights.push(cur);
    }
    for (c = 0; c < cols; c++) for (gy = 0; gy < heights[c]; gy++) grid[gy][c] = true;

    /* 2. Stair-step chains climbing off the skyline, mostly on the diagonal */
    var chains = Math.max(3, Math.round(cols / 2.4));
    for (var chn = 0; chn < chains; chn++) {
      var cx = Math.floor(rnd() * cols);
      var cy = heights[cx] + (rnd() < 0.5 ? 0 : 1);
      var dx = rnd() < 0.5 ? -1 : 1;
      var steps = 1 + Math.floor(rnd() * 3);
      if (cy > heights[cx]) cx += dx;
      for (var st = 0; st <= steps; st++) {
        if (cy >= rows || cx < 0 || cx >= cols) break;
        set(cx, cy);
        var m = rnd();
        if (m < 0.55) { cx += dx; cy += 1; }
        else if (m < 0.75) { cy += 1; }
        else { cx += dx; }
      }
    }
    /* ...plus a few loners */
    for (c = 0; c < cols; c++) {
      if (rnd() < 0.14 && heights[c] + 1 < rows) set(c, heights[c] + 1 + Math.floor(rnd() * (rows - heights[c] - 1)));
    }

    function filled(x, y) {
      if (y < 0) return true;
      if (x < 0) return filled(0, y);
      if (x >= cols) return filled(cols - 1, y);
      if (y >= rows) return false;
      return grid[y][x];
    }

    /* 3. Rounded holes of the current colour punched just under the skyline (not on full panels, where content sits) */
    for (c = 1; o.mass !== 'full' && c < cols - 1; c++) {
      for (gy = Math.max(1, heights[c] - 2); gy < heights[c]; gy++) {
        if (filled(c - 1, gy) && filled(c + 1, gy) && filled(c, gy - 1) && filled(c, gy + 1) &&
            !holes[gy][c - 1] && !holes[gy - 1][c] && rnd() < 0.3) {
          holes[gy][c] = true;
        }
      }
    }

    var sgn = dir === 'up' ? 1 : -1;
    function yPos(g) { return dir === 'up' ? g * rowH : h - (g + 1) * rowH; }
    function vY(v) { return dir === 'up' ? v * rowH : h - v * rowH; }

    /*
      Vertex pass. Around every grid corner, look at the 4 cells:
      1 filled        -> that corner is rounded (convex)
      3 filled        -> liquid fillet into the empty cell (concave)
      2 on a diagonal -> usually a liquid bridge (fillets both sides), sometimes two separate rounded corners
    */
    var roundV = [], fillets = [];
    for (var vy = 0; vy <= rows; vy++) {
      roundV.push([]);
      for (var vx = 0; vx <= cols; vx++) {
        var q = [[-1, -1], [0, -1], [-1, 0], [0, 0]];
        var f = q.map(function (o) { return filled(vx + o[0], vy + o[1]); });
        var cnt = f.filter(Boolean).length;
        var diag = cnt === 2 && f[0] === f[3];
        var bridge = diag && hash(vx + 71, vy + 37, seed) < 0.7;
        roundV[vy].push(cnt === 1 || (diag && !bridge));
        if (cnt === 3 || bridge) {
          q.forEach(function (o, qi) {
            if (f[qi]) return;
            fillets.push({ x: vx * cell, y: vY(vy), ex: o[0] === -1 ? -1 : 1, ey: (o[1] === -1 ? -1 : 1) * sgn });
          });
        }
      }
    }

    function neighbours(x, y) {
      for (var oy = -1; oy <= 1; oy++) for (var ox = -1; ox <= 1; ox++) {
        if ((ox || oy) && filled(x + ox, y + oy) && x + ox >= 0 && x + ox < cols) return true;
      }
      return false;
    }

    var blocks = [], holeList = [], map = {};
    for (gy = 0; gy < rows; gy++) {
      for (c = 0; c < cols; c++) {
        if (!grid[gy][c]) continue;
        var topV = dir === 'up' ? gy : gy + 1, botV = dir === 'up' ? gy + 1 : gy;
        var sw = 1, sh = 1, jx = 0, jy = 0, iso = !neighbours(c, gy);
        if (iso) {
          /* Loose blocks: similar, never identical — size, proportion and placement vary a touch */
          var base = 0.8 + rnd() * 0.2;
          sw = base * (0.92 + rnd() * 0.16);
          sh = base * (0.92 + rnd() * 0.16);
          jx = (rnd() - 0.5) * cell * 0.14;
          jy = (rnd() - 0.5) * rowH * 0.14;
        }
        var rad = r * Math.min(sw, sh);
        blocks.push(map[c + ',' + gy] = {
          c: c, gy: gy, iso: iso,
          x: c * cell + (cell - cell * sw) / 2 + jx,
          y: yPos(gy) + (rowH - rowH * sh) / 2 + jy,
          w: cell * sw, h: rowH * sh,
          rad: [roundV[topV][c], roundV[topV][c + 1], roundV[botV][c + 1], roundV[botV][c]].map(function (k) { return k ? rad : 0; })
        });
        if (holes[gy][c]) {
          var hs = 0.66 + rnd() * 0.26;
          var hw = cell * hs * (0.92 + rnd() * 0.16), hh = rowH * hs * (0.92 + rnd() * 0.16);
          holeList.push({
            c: c, gy: gy,
            x: c * cell + (cell - hw) / 2 + (rnd() - 0.5) * cell * 0.12,
            y: yPos(gy) + (rowH - hh) / 2 + (rnd() - 0.5) * rowH * 0.12,
            w: hw, h: hh, rad: r * hs
          });
        }
      }
    }

    return {
      w: w, h: h, cell: cell, rowH: rowH, r: r, rf: rf, dir: dir, color: color, ink: ink,
      zone: zone, backdrop: backdrop, el: el,
      animate: animate, style: style, seed: seed, cols: cols, rows: rows, map: map,
      grid: !!o.grid, measure: o.measure !== false,
      blocks: blocks, holes: holeList, fillets: fillets
    };
  }

  function canvasFor(el, L) {
    var canvas = el.querySelector('canvas');
    if (!canvas) {
      canvas = document.createElement('canvas');
      el.appendChild(canvas);
    }
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var cw = Math.round(L.w * dpr), chh = Math.round(L.h * dpr);
    if (canvas.width !== cw || canvas.height !== chh) { canvas.width = cw; canvas.height = chh; }
    var ctx = canvas.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { canvas: canvas, ctx: ctx, dpr: dpr };
  }

  /*
    Turn whatever has been drawn in the fill colour into the real finish: the
    section's picture (lined up with it exactly) plus its tint, or the colour.
    Then punch the holes out so they show what is behind.
  */
  function finishFill(ctx, L) {
    var z = L.zone;
    if (z && L.el && z.img && z.img.complete && z.img.naturalWidth) {
      var br = z.bg.getBoundingClientRect(), er = L.el.getBoundingClientRect();
      var iw = z.img.naturalWidth, ih = z.img.naturalHeight;
      var sc = Math.max(br.width / iw, br.height / ih), w = iw * sc, h = ih * sc;
      ctx.globalCompositeOperation = 'source-in';
      ctx.drawImage(z.img, (br.width - w) / 2 - (er.left - br.left), (br.height - h) / 2 - (er.top - br.top), w, h);
      if (z.overlay && z.overlay.alpha > 0) {
        ctx.globalCompositeOperation = 'source-atop';
        ctx.globalAlpha = z.overlay.alpha;
        ctx.fillStyle = z.overlay.colour;
        ctx.fillRect(0, 0, L.w, L.h);
        ctx.globalAlpha = 1;
      }
    }
    ctx.globalCompositeOperation = 'destination-out';
    var hp = new Path2D();
    L.holes.forEach(function (hl) { roundedCellR(hp, hl.x, hl.y, hl.w, hl.h, hl.rad, hl.rad, hl.rad, hl.rad); });
    ctx.fill(hp);
    ctx.globalCompositeOperation = 'source-over';
  }
  function paintColour(L) {
    var z = L.zone;
    if (!z) return L.color;
    var p = parseColor(z.colour);
    return p && p.a > 0.5 ? z.colour : '#0B1138';
  }

  /* The finished shape on a transparent canvas */
  function paintFinal(ctx, L) {
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
    ctx.clearRect(0, 0, L.w, L.h);
    ctx.fillStyle = paintColour(L);
    ctx.fill(clipAll(L));
    finishFill(ctx, L);
  }

  function clipAll(L) {
    if (L._clip) return L._clip;
    var p = new Path2D();
    L.blocks.forEach(function (b) { roundedCellR(p, b.x, b.y, b.w, b.h, b.rad[0], b.rad[1], b.rad[2], b.rad[3]); });
    L.fillets.forEach(function (f) { filletPath(p, f.x, f.y, f.ex, f.ey, L.rf); });
    return (L._clip = p);
  }

  /* ---------------- outline tracing ---------------- */
  /*
    Outline of the finished shape as chains of corner points, traced on the grid
    with the filled side kept on the left. Where two blocks only touch at a
    corner, the trace follows the liquid bridge (or keeps them apart) exactly as
    the finished shape does. Loose blocks and holes get their own loops.
  */
  function contour(L) {
    var cols = L.cols, rows = L.rows, seed = L.seed;
    function F(c, y) {
      if (y < 0) return true;
      if (c < 0) c = 0;
      if (c >= cols) c = cols - 1;
      if (y >= rows) return false;
      var b = L.map[c + ',' + y];
      return !!b && !b.iso;
    }
    var edges = [], out = {}, inc = {};
    function add(ax, ay, bx, by) {
      var e = { a: [ax, ay], b: [bx, by], d: [bx - ax, by - ay], used: false };
      edges.push(e);
      (out[ax + ',' + ay] = out[ax + ',' + ay] || []).push(e);
      inc[bx + ',' + by] = (inc[bx + ',' + by] || 0) + 1;
    }
    L.blocks.forEach(function (b) {
      if (b.iso) return;
      var c = b.c, y = b.gy;
      if (!F(c, y - 1)) add(c, y, c + 1, y);
      if (!F(c, y + 1)) add(c + 1, y + 1, c, y + 1);
      if (!F(c - 1, y)) add(c, y + 1, c, y);
      if (!F(c + 1, y)) add(c + 1, y, c + 1, y + 1);
    });
    function walk(e0) {
      var pts = [e0.a.slice()], e = e0;
      while (e && !e.used) {
        e.used = true;
        pts.push(e.b.slice());
        var cand = (out[e.b[0] + ',' + e.b[1]] || []).filter(function (x) { return !x.used; });
        if (!cand.length) break;
        if (cand.length === 1) { e = cand[0]; continue; }
        /* corner-only touch: a bridged pair is one piece (turn right), otherwise two (turn left) */
        var want = hash(e.b[0] + 71, e.b[1] + 37, seed) < 0.7 ? -1 : 1, cur = e;
        e = cand.filter(function (x) { return Math.sign(cur.d[0] * x.d[1] - cur.d[1] * x.d[0]) === want; })[0] || cand[0];
      }
      return pts;
    }
    var raws = [];
    edges.forEach(function (e) { if (!e.used && !inc[e.a[0] + ',' + e.a[1]]) raws.push({ pts: walk(e), closed: false }); });
    edges.forEach(function (e) { if (!e.used) raws.push({ pts: walk(e), closed: true }); });

    function X(vx) { return vx * L.cell; }
    function Y(vy) { return L.dir === 'up' ? vy * L.rowH : L.h - vy * L.rowH; }
    var chains = [];
    raws.forEach(function (rc) {
      var p = rc.pts;
      if (rc.closed) p = p.slice(0, p.length - 1);
      var q = [];
      for (var i = 0; i < p.length; i++) {
        if (!rc.closed && (i === 0 || i === p.length - 1)) { q.push(p[i]); continue; }
        var prev = p[(i - 1 + p.length) % p.length], next = p[(i + 1) % p.length];
        var d1 = [p[i][0] - prev[0], p[i][1] - prev[1]], d2 = [next[0] - p[i][0], next[1] - p[i][1]];
        if (d1[0] * d2[1] - d1[1] * d2[0] !== 0) q.push(p[i]);
      }
      if (q.length < 2) return;
      var rad = q.map(function (v, i) {
        if (!rc.closed && (i === 0 || i === q.length - 1)) return 0;
        var prev = q[(i - 1 + q.length) % q.length], next = q[(i + 1) % q.length];
        var cr = (v[0] - prev[0]) * (next[1] - v[1]) - (v[1] - prev[1]) * (next[0] - v[0]);
        return cr > 0 ? L.r : L.rf;
      });
      chains.push(mkChain(q.map(function (v) { return [X(v[0]), Y(v[1])]; }), rad, rc.closed, 'grid'));
    });
    L.blocks.forEach(function (b) {
      if (!b.iso) return;
      chains.push(mkChain([[b.x, b.y], [b.x + b.w, b.y], [b.x + b.w, b.y + b.h], [b.x, b.y + b.h]], b.rad.slice(), true, 'loose'));
    });
    L.holes.forEach(function (hl) {
      chains.push(mkChain([[hl.x, hl.y], [hl.x + hl.w, hl.y], [hl.x + hl.w, hl.y + hl.h], [hl.x, hl.y + hl.h]], [hl.rad, hl.rad, hl.rad, hl.rad], true, 'hole'));
    });
    return chains;
  }

  function mkChain(pts, rad, closed, kind) {
    var n = pts.length, nseg = closed ? n : n - 1, segL = [], total = 0, i;
    for (i = 0; i < nseg; i++) {
      var a = pts[i], b = pts[(i + 1) % n], l = Math.hypot(b[0] - a[0], b[1] - a[1]);
      segL.push(l);
      total += l;
    }
    var ch = { pts: pts, rad: rad, closed: closed, kind: kind, n: n, nseg: nseg, segL: segL, len: total };
    ch.V = function (k) { return closed ? pts[((k % n) + n) % n] : pts[Math.max(0, Math.min(n - 1, k))]; };
    ch.R = function (k) { if (!closed && (k <= 0 || k >= n - 1)) return 0; return rad[((k % n) + n) % n]; };
    ch.P = function (t) {
      var k = Math.floor(t), f = t - k;
      if (f < 1e-9) return ch.V(k).slice();
      var p0 = ch.V(k), p1 = ch.V(k + 1);
      return [lerp(p0[0], p1[0], f), lerp(p0[1], p1[1], f)];
    };
    ch.paramAt = function (d) {
      d = Math.max(0, d);
      if (closed) d = d % total;
      for (var s = 0; s < nseg; s++) {
        if (d <= segL[s] || s === nseg - 1) return s + Math.min(1, d / (segL[s] || 1));
        d -= segL[s];
      }
      return nseg;
    };
    ch.full = subPath(ch, closed ? 0.5 : 0, closed ? n + 0.5 : nseg).path;
    if (closed) ch.full.closePath();
    /* the same outline with square corners: thickening it keeps the crisp fill's fronts straight */
    var sh = new Path2D();
    sh.moveTo(pts[0][0], pts[0][1]);
    for (i = 1; i < n; i++) sh.lineTo(pts[i][0], pts[i][1]);
    if (closed) sh.closePath();
    ch.sharp = sh;
    return ch;
  }

  /* A stretch of a chain between two params (either direction) with rounded corners */
  function subPath(ch, a, b, into) {
    var pts = [ch.P(a)], rad = [0], i;
    if (b > a) { for (i = Math.floor(a) + 1; i < b; i++) { pts.push(ch.V(i)); rad.push(ch.R(i)); } }
    else { for (i = Math.ceil(a) - 1; i > b; i--) { pts.push(ch.V(i)); rad.push(ch.R(i)); } }
    pts.push(ch.P(b));
    rad.push(0);
    var path = into || new Path2D(), len = 0, marks = [];
    if (into) path.lineTo(pts[0][0], pts[0][1]); else path.moveTo(pts[0][0], pts[0][1]);
    for (i = 1; i < pts.length; i++) {
      var p0 = pts[i - 1], p1 = pts[i];
      len += Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
      if (i < pts.length - 1) {
        var p2 = pts[i + 1];
        var d1 = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]), d2 = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
        var rr = Math.min(rad[i], d1 / 2, d2 / 2);
        if (rr > 0.1) path.arcTo(p1[0], p1[1], p2[0], p2[1], rr); else path.lineTo(p1[0], p1[1]);
        marks.push({ x: p1[0], y: p1[1], d: len });
      } else path.lineTo(p1[0], p1[1]);
    }
    return { path: path, len: len, pts: pts, marks: marks };
  }

  function pointAlong(pts, d) {
    for (var i = 1; i < pts.length; i++) {
      var l = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
      if (d <= l) {
        var f = l ? d / l : 0;
        return [lerp(pts[i - 1][0], pts[i][0], f), lerp(pts[i - 1][1], pts[i][1], f)];
      }
      d -= l;
    }
    return pts[pts.length - 1];
  }

  /* ---------------- the blueprint timeline ---------------- */
  /*
    Fill styles (--sd-d-style):
      crisp   the drawn outline thickens inward as stepped bands
      sealed  like crisp, and the section edge seals in at the same time (no last sliver)
      pour    colour rises out of the section it belongs to and fills the drawn outline
      soft    outline thickens inward with rounded fronts
      hatch   45-degree section lines sweep across and thicken until they merge
  */
  var FILLS = {
    crisp: { fill: 'stroke', sharp: true, fd: [700, 440] },
    sealed: { fill: 'stroke', sharp: true, edge: true, fd: [640, 440] },
    pour: { fill: 'pour', fd: [760, 760] },
    soft: { fill: 'stroke', fd: [820, 520] },
    hatch: { fill: 'hatch', fast: true, fd: [520, 400], lead: [110, 60] }
  };

  function blueprintSetup(L) {
    var o = FILLS[L.style] || FILLS.sealed;
    var fast = !!o.fast, lead = o.lead || [40, 20];
    var speed = fast ? 1.0 : 0.85, dMin = fast ? 180 : 200, dMax = fast ? 600 : 720;
    var loopT = fast ? [120, 320] : [140, 460], holeT = fast ? [220, 260] : [260, 320];
    var ri = 0;
    function rr() { ri++; return hash(ri * 3 + 1, ri * 5 + 2, L.seed); }
    var S = {
      o: o, clip: clipAll(L), chains: contour(L), halves: [], handles: [], guides: [],
      tw: Math.max(1.5, Math.min(2.6, L.cell * 0.05)),
      hs: Math.max(5, Math.min(9, L.cell * 0.15))
    };
    var massEnd = 0;
    S.chains.forEach(function (ch) {
      var origins = [], j;
      if (!ch.closed) {
        /* the long outline gets several pens, each drawing outwards from the middle of its stretch */
        var k = Math.max(1, Math.round(ch.len / (L.cell * 6)));
        var bounds = [0];
        for (j = 1; j < k; j++) bounds.push(ch.paramAt((j + (rr() - 0.5) * 0.4) * ch.len / k));
        bounds.push(ch.nseg);
        for (j = 0; j < k; j++) {
          var mid = ch.paramAt((j + 0.5 + (rr() - 0.5) * 0.3) * ch.len / k);
          mid = Math.max(bounds[j] + 0.01, Math.min(bounds[j + 1] - 0.01, mid));
          origins.push({ o: mid, a: bounds[j], b: bounds[j + 1], t0: rr() * 220 });
        }
      } else {
        var og = ch.paramAt(rr() * ch.len);
        var t0 = ch.kind === 'hole' ? holeT[0] + rr() * holeT[1] : loopT[0] + rr() * loopT[1];
        origins.push({ o: og, a: og - ch.nseg / 2, b: og + ch.nseg / 2, t0: t0 });
      }
      ch.te = 0;
      origins.forEach(function (org) {
        var op = ch.P(org.o);
        S.handles.push({ x: op[0], y: op[1], t: org.t0, ch: ch });
        [org.b, org.a].forEach(function (end) {
          var sp = subPath(ch, org.o, end);
          var d = Math.max(dMin, Math.min(dMax, sp.len / speed));
          S.halves.push({ ch: ch, path: sp.path, len: sp.len, pts: sp.pts, t0: org.t0, d: d });
          ch.te = Math.max(ch.te, org.t0 + d);
          sp.marks.forEach(function (m) { S.handles.push({ x: m.x, y: m.y, t: org.t0 + (m.d / (sp.len || 1)) * d, ch: ch }); });
        });
      });
      if (ch.kind === 'grid' && !ch.closed) {
        ch.tf0 = ch.te - lead[0]; ch.fd = o.fd[0]; ch.maxW = L.h * 1.5;
        massEnd = Math.max(massEnd, ch.tf0 + ch.fd);
        S.massTf0 = ch.tf0; S.massD = ch.fd; S.massMaxW = ch.maxW;
      } else if (ch.kind !== 'hole') {
        ch.tf0 = ch.te - lead[1]; ch.fd = o.fd[1]; ch.maxW = L.cell * 2.3;
      }
      /* hatching needs the area each outline encloses (the main one is closed off along the band edge) */
      if (o.fill === 'hatch' && ch.kind !== 'hole') {
        if (ch.closed) ch.region = ch.full;
        else {
          var rg = new Path2D(), first = ch.pts[0], last = ch.pts[ch.pts.length - 1], bandY = L.dir === 'up' ? -2 : L.h + 2;
          rg.moveTo(first[0], bandY);
          subPath(ch, 0, ch.nseg, rg);
          rg.lineTo(last[0], bandY);
          rg.closePath();
          ch.region = rg;
        }
      }
    });
    if (S.massD === undefined) { S.massTf0 = 0; S.massD = o.fd[0]; S.massMaxW = L.h * 1.5; }

    /* pour: one rise for everything, starting once every outline is drawn */
    if (o.fill === 'pour') {
      var allDrawn = 0;
      S.chains.forEach(function (ch) { if (ch.kind !== 'hole') allDrawn = Math.max(allDrawn, ch.te); });
      S.massTf0 = allDrawn - 80;
      S.chains.forEach(function (ch) { if (ch.kind !== 'hole') { ch.tf0 = S.massTf0; ch.fd = S.massD; } });
      massEnd = S.massTf0 + S.massD;
      S.pourLag = [];
      for (var pc = 0; pc < L.cols; pc++) S.pourLag.push(hash(pc + 5, 91, L.seed) * 0.3);
    }
    if (o.edge) {
      var ey = L.dir === 'up' ? 0 : L.h;
      S.edgePath = new Path2D();
      S.edgePath.moveTo(-L.cell, ey);
      S.edgePath.lineTo(L.w + L.cell, ey);
    }

    S.massEnd = massEnd || S.massTf0 + S.massD;
    var end = S.massEnd;
    S.chains.forEach(function (ch) {
      if (ch.kind === 'hole') { ch.tf0 = S.massEnd - 500; ch.fd = 500; }
      else end = Math.max(end, ch.tf0 + ch.fd);
    });
    S.dur = end + 120;

    /* construction guides: a few horizontal levels and vertical edges taken from the outline */
    var ys = {}, xs = {};
    S.chains.forEach(function (ch) {
      if (ch.kind === 'grid') ch.pts.forEach(function (p) { ys[Math.round(p[1])] = p; xs[Math.round(p[0])] = p; });
    });
    var yl = Object.keys(ys).map(Number).filter(function (y) { return y > 1 && y < L.h - 1; });
    var xl = Object.keys(xs).map(Number).filter(function (x) { return x > 1 && x < L.w - 1; });
    yl.slice(0, 4).forEach(function (y, i) { S.guides.push({ h: true, v: y, a: ys[y][0], t0: 40 + i * 70 + rr() * 60 }); });
    var nv = Math.min(6, xl.length);
    for (var gi = 0; gi < nv; gi++) {
      var gx = xl[Math.floor((gi + 0.5) * xl.length / nv)];
      S.guides.push({ h: false, v: gx, a: xs[gx][1], t0: 80 + gi * 55 + rr() * 60 });
    }

    /* measurement redlines: the tallest step's height and one block's width */
    if (L.measure) {
      var best = null;
      S.chains.forEach(function (ch) {
        if (ch.kind !== 'grid' || ch.closed) return;
        ch.pts.forEach(function (p) {
          var dist = L.dir === 'up' ? p[1] : L.h - p[1];
          if (p[0] > L.cell && p[0] < L.w - L.cell && (!best || dist > best.dist)) best = { x: p[0], y: p[1], dist: dist };
        });
      });
      if (best) S.redline = best;
    }
    return S;
  }

  /* 45-degree section lines sweep across, then thicken until they merge into solid */
  function fillHatch(ctx, L, q) {
    var W = L.w, H = L.h, sp = Math.max(7, L.cell * 0.2), span = W + H;
    var drawQ = clamp01(q / 0.4);
    var grow = easeInOutCubic(clamp01((q - 0.24) / 0.76));
    var path = new Path2D();
    for (var c = -H; c < W + sp; c += sp) {
      var local = clamp01(drawQ * 1.5 - ((c + H) / span) * 0.5);
      if (local <= 0) continue;
      var e = easeOutCubic(local);
      path.moveTo(c + H, H);
      path.lineTo(lerp(c + H, c, e), lerp(H, 0, e));
    }
    ctx.lineCap = 'butt';
    ctx.lineWidth = lerp(1.3, sp * 1.6, grow);
    ctx.stroke(path);
  }

  /* colour rises out of its section column by column, in pixel steps, up to the drawn outline */
  function fillPour(ctx, L, S, q) {
    var up = L.dir === 'up', H = L.h, p = new Path2D();
    for (var c = 0; c < L.cols; c++) {
      var e = easeInOutCubic(clamp01((q - S.pourLag[c]) / 0.7));
      if (e <= 0) continue;
      var d = Math.ceil((e * H * 1.02) / (L.rowH / 4)) * (L.rowH / 4);   /* quarter-row steps */
      p.rect(c * L.cell - 0.5, up ? 0 : H - d, L.cell + 1, d);
    }
    ctx.fill(p);
  }

  function strokeDim(ctx, x1, y1, x2, y2, label, ink, bg, a) {
    ctx.save();
    ctx.globalAlpha = a;
    ctx.strokeStyle = ink;
    ctx.fillStyle = ink;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
    var vert = x1 === x2, t = 4;
    if (vert) { ctx.moveTo(x1 - t, y1); ctx.lineTo(x1 + t, y1); ctx.moveTo(x2 - t, y2); ctx.lineTo(x2 + t, y2); }
    else { ctx.moveTo(x1, y1 - t); ctx.lineTo(x1, y1 + t); ctx.moveTo(x2, y2 - t); ctx.lineTo(x2, y2 + t); }
    ctx.stroke();
    ctx.font = '10px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.textBaseline = 'middle';
    var tw = ctx.measureText(label).width + 8, lx, ly;
    if (vert) { lx = x1 + 6; ly = (y1 + y2) / 2 - 7; } else { lx = (x1 + x2) / 2 - tw / 2; ly = y1 - 16; }
    ctx.fillStyle = bg;
    ctx.fillRect(lx, ly, tw, 14);
    ctx.strokeRect(lx + 0.5, ly + 0.5, tw - 1, 13);
    ctx.fillStyle = ink;
    ctx.textAlign = 'left';
    ctx.fillText(label, lx + 4, ly + 7.5);
    ctx.restore();
  }

  /* One frame at time t (ms). 0 = the flat edge before anything is drawn; the end = finished shape. */
  function blueprintFrame(el, L, t) {
    var cv = canvasFor(el, L);
    if (cv.ctx) drawFrame(cv.ctx, L, t);
  }

  function drawFrame(ctx, L, t) {
    var S = L.bp;
    if (!S || t >= S.dur) { paintFinal(ctx, L); return; }
    var o = S.o, W = L.w, H = L.h, ink = L.ink, paintC = paintColour(L);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
    ctx.clearRect(0, 0, W, H);
    if (t <= 0) return;
    var massFill = clamp01((t - S.massTf0) / S.massD);
    var fadeOut = 1 - easeInOutCubic(massFill);

    /* the fill, clipped to the finished shape, then finished with the real colour or picture */
    ctx.save();
    ctx.clip(S.clip);
    ctx.fillStyle = paintC;
    ctx.strokeStyle = paintC;
    if (o.sharp) { ctx.lineJoin = 'miter'; ctx.miterLimit = 10; ctx.lineCap = 'butt'; }
    else { ctx.lineJoin = 'round'; ctx.lineCap = 'round'; }
    var accel = function (q) { return 0.35 * q + 0.65 * q * q; };
    if (o.fill === 'pour') {
      if (massFill > 0) fillPour(ctx, L, S, massFill);
    } else {
      if (o.edge && massFill > 0) {
        ctx.lineWidth = S.massMaxW * accel(massFill);
        ctx.stroke(S.edgePath);
      }
      S.chains.forEach(function (ch) {
        if (ch.kind === 'hole') return;
        var q = clamp01((t - ch.tf0) / ch.fd);
        if (q <= 0) return;
        if (o.fill === 'stroke') {
          ctx.lineWidth = ch.maxW * (o.sharp ? accel(q) : easeInOutSine(q));
          ctx.stroke(o.sharp ? ch.sharp : ch.full);
          return;
        }
        ctx.save();
        ctx.clip(ch.region);
        fillHatch(ctx, L, q);
        ctx.restore();
      });
    }
    ctx.restore();

    finishFill(ctx, L);

    /* graph paper */
    if (L.grid) {
      var ga = 0.13 * easeOutCubic(clamp01(t / 300)) * fadeOut;
      if (ga > 0.005) {
        ctx.save();
        ctx.globalAlpha = ga;
        ctx.strokeStyle = ink;
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (var gx = L.cell; gx < W; gx += L.cell) { ctx.moveTo(Math.round(gx) + 0.5, 0); ctx.lineTo(Math.round(gx) + 0.5, H); }
        for (var gy = L.rowH; gy < H - 1; gy += L.rowH) { ctx.moveTo(0, Math.round(gy) + 0.5); ctx.lineTo(W, Math.round(gy) + 0.5); }
        ctx.stroke();
        ctx.restore();
      }
    }

    /* construction guides */
    if (fadeOut > 0.01) {
      ctx.save();
      ctx.strokeStyle = ink;
      ctx.setLineDash([3, 5]);
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.5 * fadeOut;
      S.guides.forEach(function (g) {
        var q = easeOutCubic(clamp01((t - g.t0) / 520));
        if (q <= 0) return;
        ctx.beginPath();
        if (g.h) { ctx.moveTo(g.a - g.a * q, g.v + 0.5); ctx.lineTo(g.a + (W - g.a) * q, g.v + 0.5); }
        else { ctx.moveTo(g.v + 0.5, g.a - g.a * q); ctx.lineTo(g.v + 0.5, g.a + (H - g.a) * q); }
        ctx.stroke();
      });
      ctx.restore();
    }

    /* the pens drawing the outline in blueprint ink; each stroke fades as its area fills */
    ctx.save();
    ctx.strokeStyle = ink;
    ctx.lineWidth = S.tw;
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'round';
    S.halves.forEach(function (hf) {
      var q = clamp01((t - hf.t0) / hf.d);
      if (q <= 0) return;
      var a = 1 - easeInOutCubic(clamp01((t - hf.ch.tf0) / hf.ch.fd));
      if (a <= 0.01) return;
      ctx.globalAlpha = a;
      ctx.setLineDash([Math.max(0.01, easeInOutSine(q) * hf.len), hf.len + 50]);
      ctx.stroke(hf.path);
    });
    ctx.restore();

    /* measurement redline beside the tallest step */
    if (S.redline && fadeOut > 0.01) {
      var rl = S.redline, rq = easeOutCubic(clamp01((t - 260) / 360));
      if (rq > 0) {
        var edgeY = L.dir === 'up' ? 0 : H, x = rl.x + 10;
        strokeDim(ctx, x, edgeY, x, lerp(edgeY, rl.y, rq), Math.round(rl.dist) + 'px', ink, L.backdrop, 0.9 * fadeOut);
        var wy = L.dir === 'up' ? rl.y + 12 : rl.y - 12;
        if (rq >= 1) strokeDim(ctx, rl.x - L.cell, wy, rl.x, wy, L.cell + 'px', ink, L.backdrop, 0.9 * fadeOut);
      }
    }

    /* square anchor handles at each corner the pens pass, plus a larger one riding each pen tip */
    function handle(x, y, s, a) {
      if (a <= 0.01) return;
      ctx.save();
      ctx.globalAlpha = a;
      ctx.fillStyle = L.backdrop;
      ctx.fillRect(x - s / 2, y - s / 2, s, s);
      ctx.strokeStyle = ink;
      ctx.lineWidth = 1.4;
      ctx.strokeRect(x - s / 2, y - s / 2, s, s);
      ctx.restore();
    }
    S.handles.forEach(function (hd) {
      var q = clamp01((t - hd.t) / 160);
      if (q <= 0) return;
      handle(hd.x, hd.y, S.hs * lerp(0.4, 1, spring(q)), 1 - clamp01((t - hd.ch.tf0) / (hd.ch.fd * 0.6)));
    });
    S.halves.forEach(function (hf) {
      var q = (t - hf.t0) / hf.d;
      if (q <= 0 || q >= 1) return;
      var p = pointAlong(hf.pts, easeInOutSine(q) * hf.len);
      handle(p[0], p[1], S.hs * 1.35, 1);
    });
  }

  /* ---------------- playback: once per page load, in the direction of travel ---------------- */
  var SLOW = 1.5;   /* playback runs this much slower than the timeline's own timings */
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var scrollDir = 1, lastY = window.pageYOffset;
  window.addEventListener('scroll', function () {
    var y = window.pageYOffset;
    if (y !== lastY) { scrollDir = y > lastY ? 1 : -1; lastY = y; }
  }, { passive: true });

  function paint(el) {
    var wrap = el.closest('[data-gd-auto-fill]');
    if (wrap) applyAutoFill(wrap, el);
    var L = layoutDither(el);
    if (!L) return;
    L.bp = blueprintSetup(L);
    el._gdLayout = L;
    if (!L.animate || reduceMotion) { el._bpPlayed = true; el._bpP = 1; }
    if (el._bpP === undefined) {
      /* starting state from where the strip sits when the page loads */
      el._bpP = el.getBoundingClientRect().bottom <= 0 ? 1 : 0;
    }
    blueprintFrame(el, L, el._bpP * L.bp.dur);
  }

  function setState(el, p) {
    el._bpDir = 0;
    el._bpP = p;
    var L = el._gdLayout;
    if (L) blueprintFrame(el, L, p * L.bp.dur);
  }

  function play(el) {
    var L = el._gdLayout;
    if (!L || el._bpPlayed || el._bpDir === 1) return;
    el._bpPlayed = true;
    el._bpDir = 1;
    el._bpP = 0;
    var last = null;
    function frame(now) {
      if (el._bpDir !== 1) return;
      var cur = el._gdLayout;
      if (!cur) { el._bpDir = 0; return; }
      if (last === null) last = now;
      var dt = Math.min(now - last, 64);
      last = now;
      el._bpP = clamp01(el._bpP + dt / (cur.bp.dur * SLOW));
      blueprintFrame(el, cur, el._bpP * cur.bp.dur);
      if (el._bpP < 1) requestAnimationFrame(frame); else el._bpDir = 0;
    }
    requestAnimationFrame(frame);
  }

  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var el = e.target;
      if (el._bpPlayed || !el._gdLayout) return;
      if (!e.isIntersecting) {
        /* left below the screen without playing: ready to draw when scrolled down to again */
        if (e.boundingClientRect.top > 0) setState(el, 0);
        el._bpSeen = true;
        return;
      }
      if (e.intersectionRatio < 0.35) return;
      var first = !el._bpSeen;
      el._bpSeen = true;
      if ((first || scrollDir > 0) && el._bpP < 1) play(el);
      else setState(el, 1);
    });
  }, { threshold: [0, 0.35] }) : null;

  var ro = window.ResizeObserver ? new ResizeObserver(function (entries) {
    entries.forEach(function (en) {
      var el = en.target;
      if (el._gdW === el.clientWidth && el._gdH === el.clientHeight) return;
      el._gdW = el.clientWidth; el._gdH = el.clientHeight;
      paint(el);
    });
  }) : null;

  function mount(el) {
    if (el._gdMounted) { paint(el); return; }
    el._gdMounted = true;
    paint(el);
    el._gdW = el.clientWidth; el._gdH = el.clientHeight;
    if (io && el._gdLayout && el._gdLayout.animate && !reduceMotion) io.observe(el);
    else { el._bpPlayed = true; setState(el, 1); }
    if (ro) ro.observe(el);
  }

  function mountAll(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-gd-dither]'), mount);
  }

  /* repaint auto-coloured strips when the page around them changes */
  function refreshAuto() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-gd-auto-fill] [data-gd-dither]'), function (el) {
      if (el._gdMounted) paint(el);
    });
  }

  /* play a strip again from the start (used by previews) */
  function replay(el) {
    if (!el._gdLayout) paint(el);
    el._bpPlayed = false;
    el._bpDir = 0;
    setState(el, 0);
    play(el);
  }

  /* draw one strip frozen at t ms (used by previews) */
  function frameAt(el, t) {
    if (!el._gdLayout) paint(el);
    el._bpPlayed = true;
    el._bpDir = 0;
    if (el._gdLayout) blueprintFrame(el, el._gdLayout, t);
    return el._gdLayout ? el._gdLayout.bp.dur : 0;
  }

  /*
    Draw the blueprint on any canvas you control (e.g. the mobile menu):
      var p = GDBP.painter({ w, h, cell, rows, dir: 'up', mass: 'full', color, seed });
      p.draw(ctx, t)  // t from 0 to p.dur (ms); ctx already scaled for the device
  */
  function painter(o) {
    var L = layoutFrom({
      w: o.w, h: o.h, cell: o.cell || 32, radius: o.radius == null ? 22 : o.radius,
      rows: o.rows || Math.max(3, Math.ceil(o.h / (o.cell || 32))), dir: o.dir || 'up', mass: o.mass,
      color: o.color || '#0048FF', style: o.style || 'sealed', backdrop: o.backdrop || pageBg(),
      ink: o.ink || 'auto', seed: o.seed || 7, measure: o.measure !== false, grid: !!o.grid
    });
    L.bp = blueprintSetup(L);
    return { dur: L.bp.dur, draw: function (ctx, t) { drawFrame(ctx, L, t); } };
  }

  window.GDBP = { mount: mount, mountAll: mountAll, refresh: refreshAuto, replay: replay, frameAt: frameAt, painter: painter };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { mountAll(document); });
  else mountAll(document);
  window.addEventListener('load', refreshAuto);
  /* night mode (html class change) changes the backdrop and ink: redraw every strip */
  if (window.MutationObserver) {
    var wasDark = document.documentElement.classList.contains('gd-dark-mode');
    new MutationObserver(function () {
      var dark = document.documentElement.classList.contains('gd-dark-mode');
      if (dark === wasDark) return;
      wasDark = dark;
      Array.prototype.forEach.call(document.querySelectorAll('[data-gd-dither]'), function (el) { if (el._gdMounted) paint(el); });
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }
  if (!window.ResizeObserver) window.addEventListener('resize', function () { mountAll(document); });
  document.addEventListener('shopify:section:load', function (e) { mountAll(e.target); refreshAuto(); });
  document.addEventListener('shopify:section:unload', function () { setTimeout(refreshAuto, 50); });
  document.addEventListener('shopify:section:reorder', function () { setTimeout(refreshAuto, 50); });
})();
